/* =========================================================
   Welcome Pack — DermX Tattoo

   Bloque de conversión: tres animaciones de disparo único, ninguna con pin ni
   scrub, y cada una con su propio ScrollTrigger aunque las tres compartan el
   mismo trigger (la sección). Así se puede retocar el timing de una sin mover
   las otras:

   1. Reveal del bloque de oferta (título + precios + CTA): fade-in + slide-up
      al 80% del viewport.
   2. Tachado del precio de comparación: scaleX 0 -> 1 sobre una barra absoluta
      con transform-origin izquierdo, con un retardo respecto al reveal para
      que el precio anterior se vea intacto un instante antes de tacharse.
   3. Ventajas: mismo fade-in + slide-up, pero con stagger, para que se lean
      como una secuencia y no como un bloque.

   Los tres usan toggleActions "play none none reverse": si el usuario sube y
   vuelve a bajar, el reveal se repite.

   Con prefers-reduced-motion: reduce no se crea ningún ScrollTrigger — el CSS
   ya deja todo visible y el precio tachado de forma estática.

   Comparte con brand-teaser.js / split-reveal.js la promesa
   window.dermxGsapReady, así que GSAP se descarga una única vez por página.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo. */
  if (window.DermxWelcomePack) {
    window.DermxWelcomePack.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var MOTION_OK = '(prefers-reduced-motion: no-preference)';

  /* Una instancia por section.id: { mm } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* Promesa compartida: si otra sección de la home ya pidió GSAP, se reutiliza
     y no se inyecta el script por duplicado. */
  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[welcome-pack] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[welcome-pack] ScrollTrigger no disponible');
        // registerPlugin es idempotente, pero comprobarlo evita repetir el
        // trabajo interno cada vez que se recarga una sección en el editor.
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        // En móvil, mostrar/ocultar la barra de direcciones cambia innerHeight:
        // sin esto ScrollTrigger recalcularía a media animación y daría saltos.
        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, 60);
  }

  /* Las imágenes que aún no han cargado cambian el layout (y con él el punto
     de disparo): refrescamos cuando llegan. */
  function refreshOnMedia(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* Los tiempos viajan en ms desde el schema. */
  function seconds(value, fallback) {
    var parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) return fallback;
    return parsed / 1000;
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;

    // revert() mata tweens, sets y ScrollTriggers de esta sección y devuelve
    // los estilos inline: sin esto, editar en vivo dejaría triggers duplicados
    // disparando el mismo reveal varias veces.
    instance.mm.revert();
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP mostramos el estado final —contenido visible y precio ya
        // tachado—: la sección sigue vendiendo aunque no se anime.
        root.classList.add('welcome-pack--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var offer = root.querySelector('[data-welcome-pack-offer]');
    var strike = root.querySelector('[data-welcome-pack-strike]');
    var advantages = root.querySelectorAll('[data-welcome-pack-advantage]');

    var strikeDelay = seconds(root.dataset.strikeDelay, 0.35);
    var strikeDuration = seconds(root.dataset.strikeDuration, 0.45);
    var iconStagger = seconds(root.dataset.iconStagger, 0.15);

    var mm = gsap.matchMedia();
    instances.set(sectionId, { mm: mm });

    /* Un único contexto: con movimiento reducido no se registra nada y manda
       el CSS (todo visible, barra del tachado a scaleX(1)). El comportamiento
       es el mismo en cualquier tamaño de pantalla —sin pin ni scrub no hay
       nada que ajustar por breakpoint—, así que no hace falta separar móvil. */
    mm.add(MOTION_OK, function () {
      var tweens = [];

      /* --- 1. Reveal del bloque de oferta --- */
      if (offer) {
        tweens.push(
          gsap.fromTo(
            offer,
            { autoAlpha: 0, y: 40 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              ease: 'power2.out',
              overwrite: 'auto',
              scrollTrigger: {
                id: 'welcome-pack-offer-' + sectionId,
                trigger: root,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          )
        );
      }

      /* --- 2. Tachado animado del precio de comparación ---
         ScrollTrigger propio, no un `.to()` encadenado al reveal: el retardo
         se controla desde el schema sin tocar la duración del reveal, y si
         mañana hay que dispararlo en otro punto basta con cambiar el `start`
         de aquí. transform-origin: left lo pone el CSS; GSAP solo mueve
         scaleX, que es la única transformación del elemento. */
      if (strike) {
        tweens.push(
          gsap.fromTo(
            strike,
            { scaleX: 0 },
            {
              scaleX: 1,
              duration: strikeDuration,
              delay: strikeDelay, // el precio se ve intacto este instante
              ease: 'power1.inOut',
              overwrite: 'auto',
              scrollTrigger: {
                id: 'welcome-pack-strike-' + sectionId,
                trigger: root,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          )
        );
      }

      /* --- 3. Ventajas con stagger --- */
      if (advantages.length) {
        tweens.push(
          gsap.fromTo(
            advantages,
            { autoAlpha: 0, y: 24 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              stagger: iconStagger,
              overwrite: 'auto',
              scrollTrigger: {
                id: 'welcome-pack-advantages-' + sectionId,
                trigger: root,
                // Un pelín más tarde que la oferta: primero el precio, después
                // los argumentos que lo acompañan.
                start: 'top 75%',
                toggleActions: 'play none none reverse',
              },
            }
          )
        );
      }

      return function () {
        tweens.forEach(function (tween) {
          if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
          tween.kill();
        });
      };
    });

    refreshOnMedia(root, ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.welcome-pack[data-section-id]').forEach(init);
  }

  window.DermxWelcomePack = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    if (window.gsap && isPluginRegistered('ScrollTrigger')) {
      refreshSoon(window.gsap.core.globals().ScrollTrigger);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.welcome-pack[data-section-id]');
    if (!root) return;
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('.welcome-pack[data-section-id]');
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  // Al seleccionar/deseleccionar en el editor cambian offsets y alturas.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach(function (
    eventName
  ) {
    document.addEventListener(eventName, function () {
      if (window.gsap && isPluginRegistered('ScrollTrigger')) {
        refreshSoon(window.gsap.core.globals().ScrollTrigger);
      }
    });
  });
})();
